import { useState, type ReactNode } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import { MenuRow } from './MenuRow';

export type OptionListItem = {
  key: string;
  label: string;
  icon?: string;
};

type Props = {
  icon: ReactNode;
  label: string;
  valueLabel: string;
  items: OptionListItem[];
  onSelect: (key: string) => void;
  isTabletUp: boolean;
  disabled?: boolean;
};

export function OptionListRow({
  icon,
  label,
  valueLabel,
  items,
  onSelect,
  isTabletUp,
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const isSelectable = items.length > 1;

  const row = (
    <MenuRow
      icon={icon}
      label={label}
      disabled={!isSelectable || disabled}
      className={cn(!isSelectable && 'cursor-default hover:bg-transparent')}
      right={
        <>
          <span>{valueLabel}</span>
          {isSelectable && (
            <ChevronDownIcon
              className={cn(
                'size-[1.6rem] text-tertiary transition-transform',
                open && 'rotate-180'
              )}
            />
          )}
        </>
      }
    />
  );

  if (!isSelectable) {
    return row;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{row}</PopoverTrigger>
      <PopoverContent
        side={isTabletUp ? 'right' : 'top'}
        align="end"
        sideOffset={isTabletUp ? 12 : 4}
        collisionPadding={12}
        className="w-auto rounded-[1.2rem] bg-background/[0.94] backdrop-blur-[0.4rem] border-stroke p-[0.4rem] shadow-dropdown!">
        <ul className="flex flex-col gap-px">
          {items.map(item => (
            <li key={item.key}>
              <button
                type="button"
                onClick={() => {
                  onSelect(item.key);
                  setOpen(false);
                }}
                className="w-full h-[3.6rem] pl-[1.2rem] pr-[1.6rem] flex items-center gap-[0.8rem] rounded-[0.8rem] medium5 text-primary cursor-pointer hover:bg-secondary-bg transition-colors">
                {item.icon && (
                  <img
                    src={item.icon}
                    alt=""
                    className="size-[2rem] rounded-full shrink-0"
                  />
                )}
                <span className="whitespace-nowrap">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
